import { memo, useCallback, useEffect, useMemo, useState } from 'react'

// **MUI Imports

// **Components Imports
import CustomDialog from '../../../../components/dialog'
import Icon from '../../../../components/icon'

// **Utils
import { checkPublicRoute, showErrorToast } from '../../../../utils/utils'

// **API
import { getStagesHistory } from '../../../../axios/api/candidate'

// Component
import { Box } from '@mui/material'
import CandidateStageHistory from './CandidateStageHistory' // adjust path as needed
import CreateShareableLink from './CreateShareableLink'

const CandidateStageHistoryModal = ({
  open,
  title,
  handleClose,
  contestId,
  candidateId,
  assessment = false
}) => {
  // state
  const [data, setData] = useState({
    candidateStageDetails: [],
    publicLinkMetadata: {}
  })
  const [loading, setLoading] = useState(false)
  const [existingUuid, setExistingUuid] = useState({
    publicLinkToken: null,
    publicLinkExpiryTime: null
  })

  const isPublicRoute = useMemo(() => {
    return checkPublicRoute()
  }, [])

  // handlers

  const fetchStagesData = useCallback(async (candidateId, contestId) => {
    setLoading(true)
    const response = await getStagesHistory(candidateId, contestId)
    setLoading(false)

    if (response?.isError) {
      showErrorToast(response)
      setData({})
    } else {
      setData({
        candidateStageDetails: response?.candidateStageDetails || [],
        publicLinkMetadata: response?.publicLinkMetadata || {}
      })
      setExistingUuid({
        publicLinkToken: response?.publicLinkToken || null,
        publicLinkExpiryTime: response?.publicLinkExpiryTime || null
      })
    }
  }, [])

  const handleRefresh = useCallback(() => {
    if (candidateId && contestId) {
      fetchStagesData(candidateId, contestId)
    }
  }, [candidateId, contestId, fetchStagesData])

  // effects
  useEffect(() => {
    if (open && candidateId && contestId) {
      fetchStagesData(candidateId, contestId)
    }
  }, [open, candidateId, contestId, fetchStagesData])

  useEffect(() => {
    if (!open) {
      setLoading(false)
      setData({})
    }
  }, [open])

  const renderHeader = useCallback(() => {
    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 4 }}>
        {!isPublicRoute && (
          <CreateShareableLink
            candidateId={candidateId}
            contestId={contestId}
            existingUuid={existingUuid}
            loading={loading}
            publicLinkMetadata={data?.publicLinkMetadata}
          />
        )}
        <Icon
          onClick={handleRefresh}
          icon={'mdi:refresh'}
          hoverEffect
          title="Refresh"
          hoverBoxStyle={{
            padding: '5px'
          }}
        />
      </Box>
    )
  }, [
    loading,
    handleRefresh,
    existingUuid,
    candidateId,
    contestId,
    data?.publicLinkMetadata,
    isPublicRoute
  ])

  return (
    <CustomDialog
      open={open}
      title={title || 'Candidate Stages History'}
      handleClose={handleClose}
      className="stages-history-modal"
      dialogPaperStyle={{
        width: '85%',
        maxWidth: '1200px',
        maxHeight: '70%',
        '& .MuiDialogContent-root': {
          padding: '0px !important',
          display: 'flex'
        }
      }}
      renderHeader={renderHeader}
    >
      <CandidateStageHistory
        assessment={assessment}
        data={data?.candidateStageDetails}
        loading={loading}
        contestId={contestId}
        onRefresh={handleRefresh}
        showRefreshButton={false}
        tableHeight="100%"
      />
    </CustomDialog>
  )
}

export default memo(CandidateStageHistoryModal)
